import {Tr} from "@/i18n/i18n";
import {ResourceType} from "@/store/place/types";
import {Amount} from "@/model/amount";
import {Keys} from "@/constants/key";
import {Player} from "@/store/player/types";
import {GameState} from "@/constants/game-state";
import TaskUtils from "@/utils/task-utils";

export default class ResourceUtils {

    public static findRewardTypeIcon(rewardType: string) {
        switch (rewardType) {
            case 'gold':
                return Keys.IMG_REWARD_GOLD;
            case 'crystal':
                return Keys.IMG_REWARD_CRYSTAL_RED;
            case 'elixir':
                return Keys.IMG_REWARD_CRYSTAL_YELLOW;
            case 'mithril':
                return Keys.IMG_REWARD_GEM;
            case 'experience':
            case 'expAddition':
                return Keys.IMG_REWARD_EXPERIENCE;
            case 'sulfur':
                return Keys.IMG_RES_SULFUR;
            case 'caviar':
                return Keys.IMG_RES_CAVIAR;
            case 'cups':
                return Keys.ARENA_CUP;
            case 'attack':
                return Keys.IMG_ATTACK;
            case 'defence':
                return Keys.IMG_DEFENCE;
            case 'tickets':
                return Keys.IMG_TICKET;

        }
        throw new TypeError();
    }

    public static findRewardTypeAtlas(rewardType: string) {
        switch (rewardType) {
            case 'gold':
                return undefined;
            case 'crystal':
                return undefined;
            case 'elixir':
                return undefined;
            case 'mithril':
                return undefined;
            case 'experience':
            case 'expAddition':
                return undefined;
            case 'sulfur':
                return undefined;
            case 'cups':
                return undefined;
            case 'tickets':
                return undefined;
            case 'attack':
                return Keys.ATLAS_PROFILE;
            case 'defence':
                return Keys.ATLAS_PROFILE;
        }
        throw new TypeError();
    }

    public static getResourceAtlasUi2(resourceType: ResourceType | undefined) {
        switch (resourceType) {
            case ResourceType.SULFUR:
                return Keys.ATLAS_PLACES_10;
            case ResourceType.CAVIAR:
                return undefined;
            case ResourceType.EVENT_CURRENCY:
                return TaskUtils.getEventResourceAtlas();
        }
        return Keys.ATLAS_UI_2;
    }

    public static getResourceImageUi2(resourceType: ResourceType | undefined) {
        switch (resourceType) {
            case ResourceType.GOLD:
                return Keys.UI2_GOLD;
            case ResourceType.CRYSTAL:
                return Keys.UI2_CRYSTAL;
            case ResourceType.ELIXIR:
                return Keys.UI2_ELIXIR;
            case ResourceType.MITHRIL:
                return Keys.UI2_GEM;
            case ResourceType.SULFUR:
                return 'ametist_menu';
            case ResourceType.CAVIAR:
                return 'caviar_no_shadow';
            case ResourceType.EVENT_CURRENCY:
                return TaskUtils.getEventResourceImage();
        }
        throw new TypeError();
    }

    public static getResourceImageId(resourceType: ResourceType | undefined) {
        switch (resourceType) {
            case ResourceType.GOLD:
                return Keys.IMG_RES_GOLD;
            case ResourceType.CRYSTAL:
                return Keys.IMG_RES_CRYSTAL;
            case ResourceType.ELIXIR:
                return Keys.IMG_RES_ELIXIR;
            case ResourceType.MITHRIL:
                return Keys.IMG_RES_MITHRIL;
            case ResourceType.SULFUR:
                return Keys.IMG_RES_SULFUR;
            case ResourceType.CAVIAR:
                return Keys.IMG_RES_CAVIAR;
            case ResourceType.EVENT_CURRENCY:
                return TaskUtils.getEventResourceImage();
        }
        throw new TypeError();
    }

    public static getResourceText(resourceType: ResourceType | undefined) {
        switch (resourceType) {
            case ResourceType.GOLD:
                return Tr('zoloto');
            case ResourceType.CRYSTAL:
                return Tr('krasnie.kristalli');
            case ResourceType.ELIXIR:
                return Tr('sinie.kristalli');
            case ResourceType.MITHRIL:
                return Tr('izumrudi');
            case ResourceType.SULFUR:
                return '';
        }
        throw new TypeError();
    }

    public static getResourceImageNoShadow(resourceType: ResourceType | undefined) {
        switch (resourceType) {
            case ResourceType.GOLD:
                return Keys.IMG_RES_GOLD_NO_SHADOW;
            case ResourceType.CRYSTAL:
                return Keys.IMG_RES_CRYSTAL;
            case ResourceType.ELIXIR:
                return Keys.IMG_RES_ELIXIR_NO_SHADOW;
            case ResourceType.MITHRIL:
                return Keys.IMG_RES_GEM_NO_SHADOW;
            case ResourceType.SULFUR:
                return Keys.IMG_RES_SULFUR;
            case ResourceType.CAVIAR:
                return Keys.IMG_RES_CAVIAR;
            case ResourceType.TICKETS:
                return Keys.IMG_TICKET;
            case ResourceType.EVENT_CURRENCY:
                return TaskUtils.getEventResourceImage();
        }
        throw new TypeError();
    }

    public static convert(amount: Amount): Map<ResourceType, number> {
        return ResourceUtils.convertRes(amount.gold, amount.crystal, amount.elixir, amount.mithril, amount.sulfur);
    }

    public static convertRes(gold: number, crystal: number, elixir: number, mithril: number, sulfur: number): Map<ResourceType, number> {
        const result: Map<ResourceType, number> = new Map<ResourceType, number>();

        ResourceUtils.setIfNotZero(result, ResourceType.GOLD, gold);
        ResourceUtils.setIfNotZero(result, ResourceType.CRYSTAL, crystal);
        ResourceUtils.setIfNotZero(result, ResourceType.ELIXIR, elixir);
        ResourceUtils.setIfNotZero(result, ResourceType.MITHRIL, mithril);
        ResourceUtils.setIfNotZero(result, ResourceType.SULFUR, sulfur);

        return result;
    }

    public static convertResForTask(gold: number, crystal: number, elixir: number, mithril: number, sulfur: number, tickets: number): Map<ResourceType, number> {
        let res = ResourceUtils.convertRes(gold, crystal, elixir, mithril, sulfur);
        ResourceUtils.setIfNotZero(res, ResourceType.TICKETS, tickets);
        return res;
    }

    public static convertToBalance(player: Player): Map<ResourceType, number> {
        const result: Map<ResourceType, number> = new Map<ResourceType, number>();

        result.set(ResourceType.GOLD, player.gold);
        result.set(ResourceType.CRYSTAL, player.crystal);
        result.set(ResourceType.ELIXIR, player.elixir);
        result.set(ResourceType.MITHRIL, player.mithril);

        if (GameState.showSulfur) {
            result.set(ResourceType.SULFUR, player.sulfur);
        }

        if (GameState.showCaviar) {
            result.set(ResourceType.CAVIAR, player.caviar);
        }

        if(GameState.showEventValue && TaskUtils.getEventCurrency() > 0){
            result.set(ResourceType.EVENT_CURRENCY, TaskUtils.getEventCurrency());
        }

        return result;
    }

    private static setIfNotZero(map: Map<ResourceType, number>, key: ResourceType, value: number) {
        if (value && value !== 0) {
            map.set(key, value);
        }
    }

}
